import { compact, filter, flatten, values } from '@quromedical/utils';
import type {
  Annotation,
  ClinicalImpression,
  ClinicalImpressionInvestigation,
  Media,
  Observation,
  Reference,
} from 'fhir/r4';
import {
  ClinicalImpressionInvestigationCode,
  ClinicalImpressionNoteCode,
} from '../coding';
import type { ResourceType, WithId } from '../types';
import { filterReferencesByType } from '../utils';
import type { AuditResource } from './auditEvent';
import { mapResourceToEntry } from './bundle';
import { createCodeableConcept } from './coding';
import {
  createExtensionValue,
  ExtensionUrl,
  findExtensionValue,
  updateExtensions,
} from './extension';
import { attachBundleFullUrl, createReferenceFromBundleEntry } from './id';
import type { CreateReference } from './reference';

type NoteAuditResource = AuditResource<
  Media | Observation | ClinicalImpression
>;
const createNotePinnedExtension = createExtensionValue(
  ExtensionUrl.clinicalImpressionPinned,
);
const findPinnedExtensionValue = findExtensionValue(
  ExtensionUrl.clinicalImpressionPinned,
);

export const isNotePinned = (
  clinicalImpression: fhir4.ClinicalImpression,
): boolean => findPinnedExtensionValue(clinicalImpression.extension) || false;

export const updateNoteAuditResources = (
  baseClinicalImpression: WithId<ClinicalImpression>,
  medias: Media[],
  pinned: boolean | undefined,
): NoteAuditResource[] => {
  const mediaEntries = medias.map(mapResourceToEntry).map(attachBundleFullUrl);

  const hasMedias = mediaEntries.length > 0;

  const mediaInvestigations: ClinicalImpressionInvestigation | undefined =
    hasMedias
      ? {
          code: createCodeableConcept(
            ClinicalImpressionInvestigationCode.Media,
          ),
          item: mediaEntries.map((entry) => ({
            reference: entry.fullUrl,
          })),
        }
      : undefined;

  const updatedClinicalImpression =
    pinned !== undefined
      ? updatedPinnedStatus(baseClinicalImpression, pinned)
      : undefined;

  const currentInvestigations = baseClinicalImpression.investigation || [];

  const investigation =
    currentInvestigations || mediaInvestigations
      ? compact([...currentInvestigations, mediaInvestigations])
      : undefined;

  const result = updatedClinicalImpression || baseClinicalImpression;

  const clinicalImpression: ClinicalImpression = {
    ...result,
    investigation,
  };

  const auditResources: NoteAuditResource[] = [
    {
      method: 'PUT',
      resource: clinicalImpression,
    },
    ...mediaEntries.map<NoteAuditResource>((entry) => ({
      method: 'POST',
      resource: entry.resource,
      fullUrl: entry.fullUrl,
    })),
  ];

  return auditResources;
};

export const createNoteAuditResources = (
  baseClinicalImpression: ClinicalImpression,
  medias: Media[],
  vitals?: Observation,
): [resources: NoteAuditResource[], noteReference: CreateReference] => {
  const mediaEntries = medias.map(mapResourceToEntry).map(attachBundleFullUrl);
  const vitalEntry =
    vitals && attachBundleFullUrl(mapResourceToEntry(vitals), 'vitals');

  const vitalInvestigation: ClinicalImpressionInvestigation | undefined =
    vitalEntry && {
      code: createCodeableConcept(
        ClinicalImpressionInvestigationCode.LatestVitals,
      ),
      item: [
        {
          reference: vitalEntry?.fullUrl,
        },
      ],
    };

  const hasMedias = mediaEntries.length > 0;

  const mediaInvestigations: ClinicalImpressionInvestigation | undefined =
    hasMedias
      ? {
          code: createCodeableConcept(
            ClinicalImpressionInvestigationCode.Media,
          ),
          item: mediaEntries.map((entry) => ({
            reference: entry.fullUrl,
          })),
        }
      : undefined;

  const hasInvestigations = vitalInvestigation || mediaInvestigations;
  const investigation = hasInvestigations
    ? compact([vitalInvestigation, mediaInvestigations])
    : undefined;

  const clinicalImpression: ClinicalImpression = {
    ...baseClinicalImpression,
    investigation,
  };

  const clinicalImpressionEntry = attachBundleFullUrl(
    mapResourceToEntry(clinicalImpression),
    'clinical-impression',
  );

  const clinicalImpressionReference = createReferenceFromBundleEntry(
    clinicalImpressionEntry,
  );

  const mediaAuditResource = mediaEntries.map<NoteAuditResource>((entry) => ({
    method: 'POST',
    resource: {
      ...entry.resource,
      partOf: [{ reference: clinicalImpressionEntry.fullUrl }],
    },
    fullUrl: entry.fullUrl,
  }));

  const auditResources: (NoteAuditResource | undefined)[] = [
    {
      method: 'POST',
      resource: clinicalImpressionEntry.resource,
      fullUrl: clinicalImpressionEntry.fullUrl,
    },
    vitalEntry && {
      method: 'POST',
      resource: vitalEntry.resource,
      fullUrl: vitalEntry.fullUrl,
    },
    ...mediaAuditResource,
  ];

  return [compact(auditResources), clinicalImpressionReference];
};

export const getInvestigationReferencesByCode = (
  code: ClinicalImpressionInvestigationCode,
  referenceType: ResourceType,
  investigations: ClinicalImpressionInvestigation[],
): Reference[] => {
  const filteredInvestigations = filter(
    investigations,
    (investigation) => investigation.code.text === code,
  );
  const mappedReferences = filteredInvestigations.map(
    (investigation) => investigation.item,
  );
  const flattenReferences = flatten(mappedReferences);
  const compactedReferences = compact(flattenReferences);
  const references = values(compactedReferences);

  return filterReferencesByType(referenceType, references);
};

export const updatedPinnedStatus = (
  clinicalImpression: WithId<ClinicalImpression>,
  pinned: boolean,
): WithId<ClinicalImpression> => {
  const existingExtensions = clinicalImpression.extension;
  const pinnedExtension = createNotePinnedExtension(pinned);

  const extension = updateExtensions(
    ExtensionUrl.clinicalImpressionPinned,
    existingExtensions,
    [pinnedExtension],
  );

  return {
    ...clinicalImpression,
    extension,
  };
};

export const createAnnotation = (
  code: ClinicalImpressionNoteCode,
  text: string,
): Annotation => ({
  text,
  extension: [
    {
      url: ExtensionUrl.clinicalImpressionNoteCode,
      valueCode: code,
    },
  ],
});

export const addSubjectiveNote = (
  clinicalImpression: WithId<ClinicalImpression>,
  subjective: string,
): WithId<ClinicalImpression> => {
  const existingAnnotations = clinicalImpression.note || [];
  const newAnnotation = createAnnotation(
    ClinicalImpressionNoteCode.Subjective,
    subjective,
  );

  const note = [...existingAnnotations, newAnnotation];

  return {
    ...clinicalImpression,
    note,
  };
};
